/**
 * LineupDiary — Phase 5.E
 *
 * Per-fixture soccer refire audit. The picker fires multiple times
 * for the same fixture as the lineup state transitions ("predicted"
 * → "confirmed_xi" → "live"). Each fire produces new bets (or
 * suppresses previous ones via guardrails). This view groups bets by
 * event_id and shows the lineup_status timeline alongside the bets
 * placed at each state.
 *
 * Source: existing bets store (filtered to SOCCER). Lineup state
 * comes from the bets themselves — `lineup_status` and
 * `lineup_status_ts` fields populated by the picker at fire time.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { BetsResponse, Bet } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

interface FixtureDiary {
  event_id:    string;
  matchup:     string;
  league:      string;
  kickoff_utc: string | null;
  total_bets:  number;
  states:      Array<{
    lineup_status: string;
    earliest_ts:   string;
    bets:          Bet[];
  }>;
}

interface ViewState {
  selectedId: string | null;
  search:     string;
}
const view: ViewState = {
  selectedId: null,
  search:     "",
};

const STATE_COLORS: Record<string, string> = {
  predicted:    "var(--accent-amber)",
  confirmed_xi: "var(--accent-green-light)",
  live:         "var(--accent-blue-light)",
  late:         "var(--accent-red-light)",
  rotation:     "var(--accent-purple-light)",
};

export function renderLineupDiary(onChange: () => void): TemplateResult {
  const e = store.get<BetsResponse>("bets");
  if (e.error) return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const fixtures = buildFixtures(e.data.bets);
  const filtered = applyFilters(fixtures);

  if (!view.selectedId && filtered.length > 0) {
    view.selectedId = filtered[0]!.event_id;
  }
  const selected = filtered.find((f) => f.event_id === view.selectedId)
                ?? filtered[0]
                ?? null;

  return html`
    <div class="ldiary">
      ${headerCard(fixtures, filtered, onChange)}

      <div class="ldiary__layout">
        ${fixtureList(filtered, onChange)}
        ${fixtureDetail(selected)}
      </div>
    </div>
  `;
}

// =============================================================
// Header
// =============================================================
function headerCard(
  all: FixtureDiary[],
  filtered: FixtureDiary[],
  onChange: () => void,
): TemplateResult {
  const totalBets = all.reduce((s, f) => s + f.total_bets, 0);
  const refired = all.filter((f) => f.states.length > 1).length;
  const totalRefires = all.reduce((s, f) => s + Math.max(0, f.states.length - 1), 0);

  return html`
    <section class="panel ldiary-header">
      <div class="ldiary-header__row">
        <div class="ldiary-header__metric">
          <span class="ldiary-header__label">soccer fixtures</span>
          <span class="ldiary-header__value">${all.length}</span>
          <span class="ldiary-header__sub">${totalBets} total bets</span>
        </div>
        <div class="ldiary-header__metric">
          <span class="ldiary-header__label">refired</span>
          <span class="ldiary-header__value">${refired}</span>
          <span class="ldiary-header__sub">${totalRefires} refires across all fixtures</span>
        </div>
        <div class="ldiary-header__metric">
          <span class="ldiary-header__label">avg fires / fixture</span>
          <span class="ldiary-header__value">
            ${all.length > 0 ? (all.reduce((s, f) => s + f.states.length, 0) / all.length).toFixed(1) : "0"}
          </span>
        </div>

        <span class="ldiary-header__spacer"></span>

        <input
          class="ldiary-header__search"
          type="search"
          placeholder="Search matchup or league…"
          .value=${view.search}
          @input=${(e: Event) => {
            view.search = (e.target as HTMLInputElement).value;
            onChange();
          }}
        />

        <span class="ldiary-header__count">
          ${filtered.length} / ${all.length}
        </span>

        ${renderExplainButton({
          label: "Explain refire pattern",
          context: () => buildOverallExplainContext(all),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Fixture list (left column)
// =============================================================
function fixtureList(fixtures: FixtureDiary[], onChange: () => void): TemplateResult {
  if (fixtures.length === 0) {
    return html`
      <aside class="panel ldiary-list">
        <div class="ldiary-list__empty">
          No soccer fixtures match.
        </div>
      </aside>
    `;
  }
  return html`
    <aside class="panel ldiary-list">
      ${fixtures.map((f) => fixtureRow(f, onChange))}
    </aside>
  `;
}

function fixtureRow(f: FixtureDiary, onChange: () => void): TemplateResult {
  const isSelected = view.selectedId === f.event_id;
  const decided    = f.states.flatMap((s) => s.bets)
    .filter((b) => b.outcome === "win" || b.outcome === "loss");
  const wins       = decided.filter((b) => b.outcome === "win").length;
  const refireBadge = f.states.length > 1
    ? html`<span class="ldiary-list-row__refire">${f.states.length}×</span>`
    : "";

  return html`
    <button
      class="ldiary-list-row ${isSelected ? "is-selected" : ""}"
      @click=${() => { view.selectedId = f.event_id; onChange(); }}
    >
      <span class="ldiary-list-row__league">${shortLeague(f.league)}</span>
      <span class="ldiary-list-row__matchup">${f.matchup}</span>
      <span class="ldiary-list-row__meta">
        ${f.total_bets} bet${f.total_bets === 1 ? "" : "s"}
        ${decided.length > 0 ? html`· ${wins}/${decided.length}W` : ""}
      </span>
      ${refireBadge}
    </button>
  `;
}

function shortLeague(l: string): string {
  if (!l) return "?";
  // "England - Premier League" → "EPL"
  if (/premier/i.test(l))    return "EPL";
  if (/bundesliga/i.test(l)) return "BUN";
  if (/serie a/i.test(l))    return "SRA";
  if (/la liga/i.test(l))    return "LAL";
  if (/ligue/i.test(l))      return "LG1";
  if (/mls/i.test(l))        return "MLS";
  if (/champions/i.test(l))  return "UCL";
  return l.slice(0, 5).toUpperCase();
}

// =============================================================
// Fixture detail (right column)
// =============================================================
function fixtureDetail(f: FixtureDiary | null): TemplateResult {
  if (!f) {
    return html`
      <main class="panel ldiary-detail">
        <div class="ldiary-detail__empty">Pick a fixture from the list.</div>
      </main>
    `;
  }

  const kickoff = f.kickoff_utc ? new Date(f.kickoff_utc) : null;

  return html`
    <main class="panel ldiary-detail">
      <header class="ldiary-detail__head">
        <div class="ldiary-detail__heading">
          <h3>${f.matchup}</h3>
          <span class="ldiary-detail__sub">
            ${f.league} ·
            ${kickoff ? kickoff.toLocaleString([], {
              month: "short", day: "numeric",
              hour: "2-digit", minute: "2-digit",
            }) : "kickoff unknown"}
            · ${f.total_bets} total bets across ${f.states.length} state${f.states.length === 1 ? "" : "s"}
          </span>
        </div>
        ${renderExplainButton({
          label: "Explain this fixture",
          context: () => buildFixtureExplainContext(f),
        })}
      </header>

      <div class="ldiary-timeline">
        ${f.states.map((s, i) => stateBlock(s, i, f.states.length))}
      </div>
    </main>
  `;
}

function stateBlock(
  s: FixtureDiary["states"][number],
  i: number,
  total: number,
): TemplateResult {
  const color = STATE_COLORS[s.lineup_status] ?? "var(--text-tertiary)";
  const ts = s.earliest_ts
    ? new Date(s.earliest_ts).toLocaleString([], {
        month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

  const decided = s.bets.filter((b) => b.outcome === "win" || b.outcome === "loss");
  const wins    = decided.filter((b) => b.outcome === "win").length;

  return html`
    <section class="ldiary-state">
      <div class="ldiary-state__rail">
        <span class="ldiary-state__dot" style="background:${color};"></span>
        ${i < total - 1 ? html`<span class="ldiary-state__line"></span>` : ""}
      </div>
      <div class="ldiary-state__body">
        <header class="ldiary-state__head">
          <span class="ldiary-state__name" style="color:${color};">
            ${s.lineup_status}
          </span>
          <span class="ldiary-state__ts">${ts}</span>
          <span class="ldiary-state__spacer"></span>
          <span class="ldiary-state__counts">
            ${s.bets.length} bet${s.bets.length === 1 ? "" : "s"}
            ${decided.length > 0 ? html`<span style="color:var(--text-quaternary);"> · ${wins}/${decided.length}W</span>` : ""}
          </span>
        </header>
        <div class="ldiary-state__bets">
          ${s.bets.map(stateBetRow)}
        </div>
      </div>
    </section>
  `;
}

function stateBetRow(b: Bet): TemplateResult {
  const outcome = b.outcome ?? "pending";
  return html`
    <div class="ldiary-bet ldiary-bet--${outcome}">
      <span class="ldiary-bet__time">
        ${b.placed_at ? new Date(b.placed_at).toLocaleTimeString([], {
          hour: "2-digit", minute: "2-digit",
        }) : "—"}
      </span>
      <span class="ldiary-bet__pick">
        ${b.market ?? "—"} ${b.side ?? ""} ${b.line ?? ""}
        ${b.player ? html`· ${b.player}` : ""}
      </span>
      <span class="ldiary-bet__price">${b.price ?? "—"}</span>
      <span class="ldiary-bet__llm">
        ${b.llm_confidence ?? "—"}
      </span>
      <span class="ldiary-bet__outcome ldiary-bet__outcome--${outcome}">${outcome}</span>
      <span class="ldiary-bet__pnl ${b.pnl != null && b.pnl > 0 ? "is-pos" : b.pnl != null && b.pnl < 0 ? "is-neg" : ""}">
        ${b.pnl == null ? "—" : `${b.pnl > 0 ? "+" : ""}${b.pnl.toFixed(2)}u`}
      </span>
    </div>
  `;
}

// =============================================================
// Aggregation
// =============================================================
function buildFixtures(bets: Bet[]): FixtureDiary[] {
  const soccer = bets.filter((b) => (b.sport || "").toUpperCase() === "SOCCER");

  // Group by event_id; fall back to matchup if missing
  const groups: Record<string, Bet[]> = {};
  for (const b of soccer) {
    const k = b.event_id ?? b.matchup ?? "unknown";
    groups[k] = groups[k] ?? [];
    groups[k]!.push(b);
  }

  const fixtures: FixtureDiary[] = [];
  for (const [event_id, gbets] of Object.entries(groups)) {
    const first = gbets[0]!;
    // Group bets within fixture by lineup_status
    const stateMap: Record<string, Bet[]> = {};
    for (const b of gbets) {
      const s = b.lineup_status ?? "unknown";
      stateMap[s] = stateMap[s] ?? [];
      stateMap[s]!.push(b);
    }

    // Order states by earliest lineup_status_ts (or placed_at fallback)
    const states = Object.entries(stateMap).map(([lineup_status, bs]) => {
      const earliestTs = bs
        .map((b) => b.lineup_status_ts ?? b.placed_at ?? "")
        .filter(Boolean)
        .sort()[0] ?? "";
      // Sort bets within state by placed_at asc
      bs.sort((a, b) => (a.placed_at ?? "").localeCompare(b.placed_at ?? ""));
      return { lineup_status, earliest_ts: earliestTs, bets: bs };
    }).sort((a, b) => a.earliest_ts.localeCompare(b.earliest_ts));

    fixtures.push({
      event_id,
      matchup:     first.matchup ?? `${first.home_team ?? "?"} vs ${first.away_team ?? "?"}`,
      league:      first.league ?? "?",
      kickoff_utc: first.kickoff_utc ?? null,
      total_bets:  gbets.length,
      states,
    });
  }

  // Sort: most-recently-active fixtures first, then by kickoff
  fixtures.sort((a, b) => {
    const aLatest = a.states.at(-1)?.earliest_ts ?? "";
    const bLatest = b.states.at(-1)?.earliest_ts ?? "";
    return bLatest.localeCompare(aLatest);
  });
  return fixtures;
}

function applyFilters(fixtures: FixtureDiary[]): FixtureDiary[] {
  const q = view.search.trim().toLowerCase();
  if (!q) return fixtures;
  return fixtures.filter((f) => {
    const hay = [f.matchup, f.league, f.event_id].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });
}

// =============================================================
// Explain context
// =============================================================
function buildOverallExplainContext(fixtures: FixtureDiary[]): string {
  const refired = fixtures.filter((f) => f.states.length > 1);
  const top = [...fixtures].sort((a, b) => b.states.length - a.states.length).slice(0, 5);
  return [
    `Soccer refire pattern:`,
    `${fixtures.length} unique fixtures · ${fixtures.reduce((s, f) => s + f.total_bets, 0)} total bets`,
    `${refired.length} fixtures refired (multiple lineup-state fires)`,
    `Total refires: ${fixtures.reduce((s, f) => s + Math.max(0, f.states.length - 1), 0)}`,
    "",
    "Top 5 most-refired fixtures:",
    ...top.map((f) => `  ${f.matchup} (${shortLeague(f.league)}): ${f.states.length} states, ${f.total_bets} bets`),
  ].join("\n");
}

function buildFixtureExplainContext(f: FixtureDiary): string {
  const lines = [
    `Fixture: ${f.matchup} (${f.league})`,
    `Event ID: ${f.event_id}`,
    `Kickoff: ${f.kickoff_utc ?? "unknown"}`,
    `Total bets: ${f.total_bets} across ${f.states.length} lineup-state fire${f.states.length === 1 ? "" : "s"}`,
    "",
    "Per-state breakdown:",
  ];
  for (const s of f.states) {
    const decided = s.bets.filter((b) => b.outcome === "win" || b.outcome === "loss");
    const wins    = decided.filter((b) => b.outcome === "win").length;
    const pnl     = s.bets.reduce((sum, b) => sum + (b.pnl ?? 0), 0);
    lines.push(`  ${s.lineup_status} (first seen ${s.earliest_ts}): ${s.bets.length} bets, ${decided.length}D ${wins}W, ${pnl.toFixed(2)}u`);
    for (const b of s.bets) {
      lines.push(`    - ${b.market} ${b.side} ${b.line} @ ${b.price}: outcome=${b.outcome ?? "pending"} pnl=${b.pnl ?? "—"} llm_conf=${b.llm_confidence ?? "—"}`);
    }
  }
  return lines.join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="ldiary">
      <section class="panel" style="height:100px;"></section>
      <div class="ldiary__layout">
        <aside class="panel" style="min-height:480px;"></aside>
        <main class="panel" style="min-height:480px;"></main>
      </div>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:480px;margin:0 auto;">
        No soccer bets yet. Lineup diary populates from
        <code style="color:var(--accent-blue-light);">soccer.bets[]</code>
        in dashboard.json.
      </div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Lineups</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
